/**
 * Quiz Results Display Component
 *
 * Shows quiz score, pass/fail status, and per-question results.
 */

import type { QuizResult } from '../../lib/quizEngine';
import type { QuizSettings, FormField } from '../../types/form';
import { getCorrectAnswerDisplay } from '../../lib/quizEngine';

interface QuizResultsProps {
  result: QuizResult;
  settings: QuizSettings;
  fields: FormField[];
  onRetake?: () => void;
}

export function QuizResults({ result, settings, fields, onRetake }: QuizResultsProps) {
  const getFieldByName = (fieldName: string): FormField | undefined => {
    return fields.find((f) => f.name === fieldName);
  };

  const formatUserAnswer = (fieldName: string, answer: unknown): string => {
    const field = getFieldByName(fieldName);
    if (!field) return String(answer ?? '—');

    // For choice fields, show labels instead of values
    if (['select', 'radio', 'checkbox'].includes(field.type) && field.options) {
      if (Array.isArray(answer)) {
        return answer
          .map((val) => {
            const option = field.options?.find((opt) => opt.value === val);
            return option?.label || val;
          })
          .join(', ');
      }

      const option = field.options.find((opt) => opt.value === answer);
      return option?.label || String(answer ?? '—');
    }

    if (answer === undefined || answer === null || answer === '') {
      return '—';
    }

    return String(answer);
  };

  return (
    <div className="quiz-results max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Score Summary */}
      <div
        className={`text-center p-6 rounded-lg mb-6 ${
          result.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}
      >
        <div className="text-4xl mb-2">{result.passed ? '🎉' : '📝'}</div>

        {settings.showScore && (
          <div className="mb-2">
            <span className="text-3xl font-bold">
              {result.totalScore} / {result.maxScore}
            </span>
            {settings.showScorePercentage && (
              <span className="text-xl text-gray-600 ml-2">({result.percentage}%)</span>
            )}
          </div>
        )}

        <div
          className={`text-xl font-semibold ${
            result.passed ? 'text-green-700' : 'text-red-700'
          }`}
        >
          {result.passed ? 'Passed' : 'Not Passed'}
        </div>

        {result.passingScore > 0 && (
          <div className="text-sm text-gray-500 mt-1">
            Passing score: {result.passingScore}%
          </div>
        )}

        <p className="mt-4 text-gray-700">{result.message}</p>
      </div>

      {/* Question Results */}
      {settings.showCorrectAnswers && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
            Question Review
          </h3>

          {result.questionResults.map((qr, index) => {
            const field = getFieldByName(qr.fieldName);
            return (
              <div
                key={qr.fieldId}
                className={`p-4 rounded-lg border ${
                  qr.isCorrect
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                        qr.isCorrect
                          ? 'bg-green-500 text-white'
                          : 'bg-red-500 text-white'
                      }`}
                    >
                      {qr.isCorrect ? '✓' : '✗'}
                    </span>
                    <span className="font-medium text-gray-800">
                      Question {index + 1}: {field?.label || qr.fieldName}
                    </span>
                  </div>
                  {settings.showPointsPerQuestion && (
                    <span className="text-sm text-gray-600">
                      {qr.pointsEarned} / {qr.maxPoints} pts
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Your answer: </span>
                    <span
                      className={
                        qr.isCorrect ? 'text-green-700' : 'text-red-700 font-medium'
                      }
                    >
                      {formatUserAnswer(qr.fieldName, qr.userAnswer)}
                    </span>
                  </div>

                  {!qr.isCorrect && field && (
                    <div>
                      <span className="text-gray-600">Correct answer: </span>
                      <span className="text-green-700 font-medium">
                        {getCorrectAnswerDisplay(field, qr.correctAnswer)}
                      </span>
                    </div>
                  )}

                  {qr.explanation && (
                    <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                      <span className="text-blue-700">💡 {qr.explanation}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Retake Button */}
      {settings.allowRetake && onRetake && (
        <div className="mt-6 text-center">
          <button
            onClick={onRetake}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retake Quiz
          </button>
        </div>
      )}
    </div>
  );
}

export default QuizResults;
